import { Image } from 'antd';
import { isArray, map } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import useLazyQueryWithCallbacks from '../../../../../../../common/CommonUseLazyQuery';
import {
  API_TO_AGGREGATE_FUNCTION_LABELS,
  BREAKPOINTS,
  DATE_ISO_FORMAT,
  DEFAULT_PAGE_SIZE,
  DEFAULTDATEFORMAT,
  FALLBACK_PDF_URL,
  FIELD_TYPES,
  PROPERTY_TYPES,
  REGISTER_DETAILS_TYPES,
  ROUTES,
  TAB_KEYS,
  TIME_12_HOUR_FORMAT,
  TIME_24_HOUR_FORMAT,
} from '../../../../../../../common/constants';
import useRouter from '../../../../../../../common/useRouter';
import { toCamelCase } from '../../../../../../../common/utils';
import AggregateDropdown, {
  createAggregateHandlers,
  processRegisterStatsResults,
} from '../../../../../../../components/AggregateDropdown';
import CommonCard from '../../../../../../../components/CommonCard';
import CommonTable from '../../../../../../../components/CommonTable';
import InfiniteScrollHandler from '../../../../../../../components/InfiniteScrollHandler';
import TableFilterDropdown from '../../../../../../../components/TableFilterDropdown';
import {
  GET_REGISTER_ENTRIES,
  GET_REGISTER_ENTRIES_DROPDOWN_LIST,
  REGISTER_STATS,
} from '../../../../../graphql/Queries';
import FieldIcon from '../FieldIcon';

const EntriesTable = ({
  registerDetailsData,
  registerId,
  showListView,
  setIsDataEmpty,
  globalTeamFilter = [],
  searchValue = '',
  approvalStatus = '',
  selectedAggregates,
  setSelectedAggregates,
  showDeletedEntries = false,
  columnFilters,
  setColumnFilters,
  sortedInfo,
  setSortedInfo,
}) => {
  const {
    navigate,
    location,
    params: { projectId },
  } = useRouter();
  const [entriesData, setEntriesData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [aggregateResults, setAggregateResults] = useState({});
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const { handleAggregateChange, handleSelectAll, handleClearAll } =
    createAggregateHandlers(
      selectedAggregates,
      setSelectedAggregates,
      setAggregateResults,
    );
  const initialRegisterEntryFilter = {
    skip: 0,
    limit: DEFAULT_PAGE_SIZE,
    projectAgencyIds:
      globalTeamFilter?.length > 0 ? globalTeamFilter : undefined,
    search: searchValue || null,
  };
  const initialPaginationValue = {
    total: 0,
    current: 1,
  };
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [registerEntryFilter, setRegisterEntryFilter] = useState(
    initialRegisterEntryFilter,
  );
  const registerNavFilter = location?.state?.registerNavFilter;

  const initialRegisterStatsFilter = {
    projectAgencyIds:
      globalTeamFilter?.length > 0 ? globalTeamFilter : undefined,
    ...(columnFilters && Object?.keys(columnFilters)?.length > 0
      ? { columnFilters }
      : {}),
  };

  const [registerStatsFilter, setRegisterStatsFilter] = useState(
    initialRegisterStatsFilter,
  );

  const [getRegisterStats] = useLazyQueryWithCallbacks(REGISTER_STATS, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      processRegisterStatsResults(res, setAggregateResults);
    },
    onError() {},
  });

  const fetchRegisterStats = () => {
    if (Object?.keys(selectedAggregates)?.length > 0) {
      const aggregateData = Object?.entries(selectedAggregates)?.map(
        ([fieldId, operations]) => ({
          id: fieldId,
          operations,
        }),
      );

      getRegisterStats({
        variables: {
          data: {
            fields: aggregateData,
            registerId,
            type: REGISTER_DETAILS_TYPES.FORM_FIELD,
          },
          filter: registerStatsFilter,
        },
      });
    } else {
      setAggregateResults({});
    }
  };

  const [getRegisterEntries, { loading }] = useLazyQueryWithCallbacks(GET_REGISTER_ENTRIES, {
    fetchPolicy: 'cache-and-network',
    onError() {},
    onCompleted(res) {
      const data = res?.registerLogEntryByRegisterList?.data;
      const pagination = {
        ...paginationProp,
        total: res?.registerLogEntryByRegisterList?.total,
      };
      const result = map(data, (item, index) => {
        const obj = Object.keys(item)?.reduce((acc, curr) => {
          const newKey =
            curr === 'createdAt' ||
            curr === 'createdBy' ||
            curr === 'registerLogId'
              ? curr
              : toCamelCase(curr);
          if (curr === 'createdAt' && item[curr]) {
            acc[newKey] = moment(item[curr])?.format(DEFAULTDATEFORMAT);
          } else {
            acc[newKey] = item[curr];
          }
          return acc;
        }, {});
        obj.index = registerEntryFilter?.skip + index + 1;
        return obj;
      });
      setIsDataEmpty(!(res?.registerLogEntryByRegisterList?.total > 0));

      if (scrollFlag) {
        setEntriesData([...entriesData, ...result]);
        setScrollFlag(false);
      } else {
        setEntriesData(result);
      }
      setHasMore(!!data?.length);
      setPaginationProp(pagination);

      const total = res?.registerLogEntryByRegisterList?.total;
      if (total > 0) {
        fetchRegisterStats();
      } else if (total === 0 && Object?.keys(selectedAggregates)?.length > 0) {
        setAggregateResults({});
      }
    },
  });

  useEffect(() => {
    if (!showListView) {
      getRegisterEntries({
        variables: { registerId, filter: registerEntryFilter },
      });
    }
  }, [registerEntryFilter, showListView]);

  useEffect(() => {
    setRegisterEntryFilter((prevFilter) => ({
      ...prevFilter,
      projectAgencyIds:
        globalTeamFilter?.length > 0 ? globalTeamFilter : undefined,
    }));
    setRegisterStatsFilter((prevFilter) => ({
      ...prevFilter,
      projectAgencyIds:
        globalTeamFilter?.length > 0 ? globalTeamFilter : undefined,
    }));
  }, [globalTeamFilter]);

  useEffect(() => {
    setRegisterEntryFilter((prevFilter) => ({
      ...prevFilter,
      search: searchValue || null,
      skip: 0,
    }));
    setRegisterStatsFilter((prevFilter) => ({
      ...prevFilter,
      search: searchValue || null,
    }));
  }, [searchValue]);

  useEffect(() => {
    setRegisterEntryFilter((prevFilter) => ({
      ...prevFilter,
      approvalStatus: approvalStatus || undefined,
      skip: 0,
    }));
  }, [approvalStatus]);

  useEffect(() => {
    if (columnFilters && Object?.keys(columnFilters)?.length > 0) {
      setRegisterEntryFilter((prevFilter) => ({
        ...prevFilter,
        columnFilters: { ...columnFilters },
      }));
      setRegisterStatsFilter((prevFilter) => ({
        ...prevFilter,
        columnFilters: { ...columnFilters },
      }));
    }
  }, [columnFilters]);

  useEffect(() => {
    if (sortedInfo?.column) {
      const sortBy = {
        field: sortedInfo?.columnKey,
        order: sortedInfo?.order === 'ascend' ? 'ASC' : 'DESC',
      };
      setRegisterEntryFilter((prevFilter) => ({
        ...prevFilter,
        sortBy,
      }));
    }
  }, [sortedInfo]);

  const handleTableChange = (pagination, filters, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setPaginationProp({ ...paginationProp, ...pagination });

    const wasSorterCleared = sortedInfo?.column && !sorter?.column;

    setSortedInfo(sorter);

    let sortBy;
    if (sorter?.column) {
      sortBy = {
        field: sorter?.columnKey,
        order: sorter?.order === 'ascend' ? 'ASC' : 'DESC',
      };
    } else if (wasSorterCleared) {
      sortBy = undefined;
    } else {
      sortBy = registerEntryFilter?.sortBy;
    }

    const filteredColumnFilters = Object?.fromEntries(
      Object?.entries(filters || {})?.filter(
        ([, value]) =>
          value !== null && value !== undefined && value?.length > 0,
      ),
    );
    const hasActiveFilters = Object?.keys(filteredColumnFilters)?.length > 0;
    const newColumnFilters = hasActiveFilters
      ? filteredColumnFilters
      : undefined;

    if (filters) {
      setColumnFilters(hasActiveFilters ? filteredColumnFilters : {});
    }

    const newFilter = {
      ...registerEntryFilter,
      skip,
      limit: pagination?.pageSize,
      sortBy,
      columnFilters: newColumnFilters,
    };

    setRegisterEntryFilter(newFilter);
    setRegisterStatsFilter((prevFilter) => ({
      ...prevFilter,
      columnFilters: newColumnFilters,
    }));

    getRegisterEntries({
      variables: {
        registerId,
        filter: newFilter,
      },
    });
  };

  const handleRefetch = () => {
    getRegisterEntries({
      variables: {
        registerId,
        filter: {
          ...registerEntryFilter,
          skip: entriesData?.length,
          limit: DEFAULT_PAGE_SIZE,
        },
      },
    });
  };

  const handleRowClick = (record) => {
    const registerLogId = record?.registerLogId;
    navigate(
      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/${registerLogId}`,
      {
        state: {
          registerDetailsData,
          currentPath: location?.pathname,
          showListView,
          registerNavFilter,
          registerId,
          registerDetailsSearchValue: searchValue,
        },
      },
    );
  };

  const renderAggregateDropdown = (key, fieldType, calculationResultType) => (
    <AggregateDropdown
      fieldKey={key}
      fieldType={fieldType}
      calculationResultType={calculationResultType}
      selectedAggregates={selectedAggregates}
      onAggregateChange={(fieldKey, checkedValues) =>
        handleAggregateChange(
          fieldKey,
          checkedValues,
          getRegisterStats,
          registerId,
          REGISTER_DETAILS_TYPES.FORM_FIELD,
          registerStatsFilter,
        )
      }
      onSelectAll={(fieldKey) =>
        handleSelectAll(
          fieldKey,
          getRegisterStats,
          registerId,
          REGISTER_DETAILS_TYPES.FORM_FIELD,
          registerStatsFilter,
        )
      }
      onClearAll={(fieldKey) =>
        handleClearAll(
          fieldKey,
          getRegisterStats,
          registerId,
          REGISTER_DETAILS_TYPES.FORM_FIELD,
          registerStatsFilter,
        )
      }
      hasData={paginationProp?.total > 0}
    />
  );

  // Separate active and deleted fields
  const activeRegisterDetails = registerDetailsData?.registerDetails?.filter(
    (data) => !data?.deletedAt,
  );
  const deletedRegisterDetails = registerDetailsData?.registerDetails?.filter(
    (data) => data?.deletedAt,
  );
  const activeRegisterFormFields =
    registerDetailsData?.registerFormFields?.filter((data) => !data?.deletedAt);
  const deletedRegisterFormFields =
    registerDetailsData?.registerFormFields?.filter((data) => data?.deletedAt);

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      className: 'min-width-150',
      render: (text, record, index) => (
        <div>{registerEntryFilter?.skip + index + 1}</div>
      ),
    },
    // Active registerDetails first
    ...map(activeRegisterDetails, (data) => ({
      title: (
        <div className="d-flex">
          {data?.isRequired && <div className="required-mark">*</div>}
          <div className="mr-5">
            <FieldIcon fieldType={data?.type} />
          </div>
          <div className="mr-5">{data?.name}</div>
        </div>
      ),
      isRequired: `${data?.isRequired}`,
      dataIndex: `${toCamelCase(data?.name)}`,
      index: `${toCamelCase(data?.name)}`,
      key: data?.id,
      className: 'min-width-150',
      filteredValue: columnFilters[data?.id] || null,
      filterDropdown: (filterProps) => (
        <TableFilterDropdown
          {...filterProps}
          query={GET_REGISTER_ENTRIES_DROPDOWN_LIST}
          variables={{ registerId, fieldId: data?.id }}
          responsePath="registerFormFieldDistinctValues"
          projectAgencyIds={globalTeamFilter}
          fieldType={data?.type}
        />
      ),
      filterDropdownProps: { placement: 'bottomLeft' },
      sorter: true,
      sortOrder: sortedInfo?.columnKey === `${data?.id}` && sortedInfo?.order,
      render: (text) => {
        if (moment(text, DATE_ISO_FORMAT, true).isValid()) {
          return moment(text)?.format(DEFAULTDATEFORMAT);
        }
        if (moment(text, TIME_24_HOUR_FORMAT, true)?.isValid()) {
          return moment(text, TIME_24_HOUR_FORMAT).format(TIME_12_HOUR_FORMAT);
        }
        return <div>{text || '-'}</div>;
      },
    })),
    // Active registerFormFields
    ...map(activeRegisterFormFields, (data) => ({
      title: (
        <div>
          <div className="d-flex align-items-center">
            {data?.isRequired && <div className="required-mark">*</div>}
            <div className="mr-5">
              <FieldIcon fieldType={data?.type} />
            </div>
            <div className="mr-5">{data?.name}</div>
            {renderAggregateDropdown(
              data?.id,
              data?.type,
              data?.calculationResultType,
            )}
          </div>
          {aggregateResults?.[data?.id] && (
            <div className="aggregate-results">
              {Object?.entries(aggregateResults[data?.id])?.map(
                ([operation, result]) => (
                  <div key={operation} className="aggregate-result-item">
                    <span className="aggregate-function-name">
                      {API_TO_AGGREGATE_FUNCTION_LABELS[operation]}:
                    </span>
                    <span className="aggregate-function-value">{result}</span>
                  </div>
                ),
              )}
            </div>
          )}
        </div>
      ),
      isRequired: `${data?.isRequired}`,
      dataIndex: `${toCamelCase(data?.name)}`,
      index: `${toCamelCase(data?.name)}`,
      key: data?.id,
      className: 'min-width-150',
      filteredValue: columnFilters[data?.id] || null,
      ...(data?.type !== FIELD_TYPES.ATTACHMENT
        ? {
            filterDropdown: (filterProps) => (
              <TableFilterDropdown
                {...filterProps}
                query={GET_REGISTER_ENTRIES_DROPDOWN_LIST}
                variables={{ registerId, fieldId: data?.id }}
                responsePath="registerFormFieldDistinctValues"
                projectAgencyIds={globalTeamFilter}
                fieldType={data?.type}
              />
            ),
          }
        : {}),
      sorter: true,
      sortOrder: sortedInfo?.columnKey === `${data?.id}` && sortedInfo?.order,
      render: (text) => {
        if (moment(text, DATE_ISO_FORMAT, true)?.isValid()) {
          return moment(text)?.format(DEFAULTDATEFORMAT);
        }
        if (moment(text, TIME_24_HOUR_FORMAT, true)?.isValid()) {
          return moment(text, TIME_24_HOUR_FORMAT).format(TIME_12_HOUR_FORMAT);
        }
        if (isArray(text)) {
          return (
            <div className="d-flex align-center">
              <Image
                wrapperClassName="mr-5 mb-5"
                height={50}
                width={50}
                src={text[0]?.value}
                fallback={FALLBACK_PDF_URL}
              />
              {text?.length - 1 > 0 && (
                <span className="ml-5 pointer extra-count">
                  +{text?.length - 1}
                </span>
              )}
            </div>
          );
        }
        return text || '-';
      },
    })),
    {
      title: 'Created On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      index: 'createdAt',
      className: 'min-width-150',
      filteredValue: columnFilters?.createdAt || null,
      filterDropdown: (filterProps) => (
        <TableFilterDropdown
          {...filterProps}
          query={GET_REGISTER_ENTRIES_DROPDOWN_LIST}
          variables={{ registerId, fieldId: 'createdAt' }}
          responsePath="registerFormFieldDistinctValues"
          projectAgencyIds={globalTeamFilter}
          fieldType={PROPERTY_TYPES?.DATE}
        />
      ),
      filterDropdownProps: { placement: 'bottomLeft' },
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
    },
    // Deleted registerDetails at the end - only show if showDeletedEntries is true
    ...(showDeletedEntries
      ? map(deletedRegisterDetails, (data) => ({
          title: (
            <div className="deleted-field">
              <div className="d-flex">
                {data?.isRequired && <div className="required-mark">*</div>}
                <div className="mr-5">
                  <FieldIcon fieldType={data?.type} />
                </div>
                <div className="mr-5">{data?.name} (Deleted)</div>
              </div>
            </div>
          ),
          isRequired: `${data?.isRequired}`,
          dataIndex: `${toCamelCase(data?.name)}`,
          index: `${toCamelCase(data?.name)}`,
          className: 'min-width-150 deleted-field-column',
          render: (text) => {
            if (moment(text, DATE_ISO_FORMAT, true).isValid()) {
              return (
                <div className="deleted-field">
                  {moment(text)?.format(DEFAULTDATEFORMAT)}
                </div>
              );
            }
            return <div className="deleted-field">{text || '-'}</div>;
          },
        }))
      : []),
    // Deleted registerFormFields at the end - only show if showDeletedEntries is true
    ...(showDeletedEntries
      ? map(deletedRegisterFormFields, (data) => ({
          title: (
            <div className="deleted-field">
              <div className="d-flex align-items-center">
                {data?.isRequired && <div className="required-mark">*</div>}
                <div className="mr-5">
                  <FieldIcon fieldType={data?.type} />
                </div>
                <div className="mr-5">{data?.name} (Deleted)</div>
                {renderAggregateDropdown(
                  data?.id,
                  data?.type,
                  data?.calculationResultType,
                )}
              </div>
              {aggregateResults?.[data?.id] && (
                <div className="aggregate-results">
                  {Object?.entries(aggregateResults[data?.id])?.map(
                    ([operation, result]) => (
                      <div key={operation} className="aggregate-result-item">
                        <span className="aggregate-function-name">
                          {API_TO_AGGREGATE_FUNCTION_LABELS[operation]}:
                        </span>
                        <span className="aggregate-function-value">
                          {result}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          ),
          isRequired: `${data?.isRequired}`,
          dataIndex: `${toCamelCase(data?.name)}`,
          index: `${toCamelCase(data?.name)}`,
          className: 'min-width-150 deleted-field-column',
          render: (text) => {
            if (moment(text, DATE_ISO_FORMAT, true)?.isValid()) {
              return (
                <div className="deleted-field">
                  {moment(text)?.format(DEFAULTDATEFORMAT)}
                </div>
              );
            }
            if (moment(text, TIME_24_HOUR_FORMAT, true)?.isValid()) {
              return (
                <div className="deleted-field">
                  {moment(text, TIME_24_HOUR_FORMAT).format(
                    TIME_12_HOUR_FORMAT,
                  )}
                </div>
              );
            }
            if (isArray(text)) {
              return (
                <div className="d-flex align-center deleted-field">
                  <Image
                    wrapperClassName="mr-5 mb-5"
                    height={50}
                    width={50}
                    src={text[0]?.value}
                    fallback={FALLBACK_PDF_URL}
                  />
                  {text?.length - 1 > 0 && (
                    <span className="ml-5 pointer extra-count">
                      +{text?.length - 1}
                    </span>
                  )}
                </div>
              );
            }
            return <div className="deleted-field">{text || '-'}</div>;
          },
        }))
      : []),
  ];

  return (
    <div className="pointer">
      {isDesktopViewport ? (
        <CommonTable
          data={entriesData}
          columns={columns}
          loading={loading}
          onChange={handleTableChange}
          paginationConfig={paginationProp}
          onRow={(record) => {
            return {
              onClick: () => handleRowClick(record),
            };
          }}
          scroll={{ x: true }}
          id="register-entries-desktop-table"
        />
      ) : (
        <InfiniteScrollHandler
          scrollFlag={scrollFlag}
          refetchData={handleRefetch}
          setScrollFlag={setScrollFlag}
          hasMore={hasMore}
          loading={loading}
          dataLength={entriesData?.length}
          id="register-entries-mobile-scroll"
        >
          <div>
            {map(entriesData, (data, index) => {
              const renderFieldValue = (value) => {
                if (Array?.isArray(value)) {
                  return value?.map((imgUrl) => {
                    return (
                      <Image
                        key={imgUrl?.value}
                        wrapperClassName="mr-5 mb-5"
                        height={50}
                        width={50}
                        src={imgUrl}
                        fallback={FALLBACK_PDF_URL}
                      />
                    );
                  });
                }
                if (moment(value, DATE_ISO_FORMAT, true)?.isValid()) {
                  return moment(value)?.format(DEFAULTDATEFORMAT);
                }
                if (moment(value, TIME_24_HOUR_FORMAT, true)?.isValid()) {
                  return moment(value, TIME_24_HOUR_FORMAT)?.format(
                    TIME_12_HOUR_FORMAT,
                  );
                }
                return value || '-';
              };

              return (
                <CommonCard
                  key={index}
                  onClick={() => handleRowClick(data)}
                  id={`register-entry-card-${data?.registerLogId || index}`}
                >
                  <div className="common-card d-flex">
                    <div className="mr-5 fw-medium">
                      {registerEntryFilter?.skip + index + 1}.
                    </div>
                    <div>
                      <div className="card-content text-secondary">
                        {/* Active registerDetails fields */}
                        {map(activeRegisterDetails, (fieldDetail) => {
                          const key = toCamelCase(fieldDetail?.name);
                          const value = data?.[key];
                          return (
                            <div className="mb-15 d-flex align-center" key={key}>
                              <span className="fw-medium mr-5">
                                {fieldDetail?.isRequired && (
                                  <span className="required-mark">*</span>
                                )}
                                {fieldDetail?.name}:
                              </span>
                              <span>{renderFieldValue(value)}</span>
                            </div>
                          );
                        })}
                        {/* Active registerFormFields */}
                        {map(activeRegisterFormFields, (fieldDetail) => {
                          const key = toCamelCase(fieldDetail?.name);
                          const value = data?.[key];
                          return (
                            <div className="mb-15 d-flex align-center" key={key}>
                              <span className="fw-medium mr-5">
                                {fieldDetail?.isRequired && (
                                  <span className="required-mark">*</span>
                                )}
                                {fieldDetail?.name}:
                              </span>
                              <span>{renderFieldValue(value)}</span>
                            </div>
                          );
                        })}
                        {/* Created On */}
                        <div className="mb-15 d-flex align-center">
                          <span className="fw-medium mr-5">Created On:</span>
                          <span>{data?.createdAt || '-'}</span>
                        </div>
                        {/* Deleted registerDetails fields - only show if showDeletedEntries is true */}
                        {showDeletedEntries &&
                          map(deletedRegisterDetails, (fieldDetail) => {
                            const key = toCamelCase(fieldDetail?.name);
                            const value = data?.[key];
                            return (
                              <div
                                className="mb-15 d-flex align-center deleted-field"
                                key={key}
                              >
                                <span className="fw-medium mr-5">
                                  {fieldDetail?.isRequired && (
                                    <span className="required-mark">*</span>
                                  )}
                                  {fieldDetail?.name} (Deleted):
                                </span>
                                <span>{renderFieldValue(value)}</span>
                              </div>
                            );
                          })}
                        {/* Deleted registerFormFields - only show if showDeletedEntries is true */}
                        {showDeletedEntries &&
                          map(deletedRegisterFormFields, (fieldDetail) => {
                            const key = toCamelCase(fieldDetail?.name);
                            const value = data?.[key];
                            return (
                              <div
                                className="mb-15 d-flex align-center deleted-field"
                                key={key}
                              >
                                <span className="fw-medium mr-5">
                                  {fieldDetail?.isRequired && (
                                    <span className="required-mark">*</span>
                                  )}
                                  {fieldDetail?.name} (Deleted):
                                </span>
                                <span>{renderFieldValue(value)}</span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </CommonCard>
              );
            })}
          </div>
        </InfiniteScrollHandler>
      )}
    </div>
  );
};

export default EntriesTable;
